import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Shield, Users, DollarSign, TrendingUp, CheckCircle2, 
  Clock, AlertTriangle, Copy, QrCode, Share2, ExternalLink,
  Wallet, ArrowRight, RefreshCw, CreditCard, ChevronRight,
  Lock, Unlock, AlertCircle, UserCheck, UserX, History,
  Gift, Presentation, Scissors, Building2, Heart, Mail,
  Send, Play, Eye, Sparkles, Globe, UserPlus, ChevronDown,
  ArrowLeft, User, Star, Calendar, Smartphone, BookOpen,
  Target, Zap, HelpCircle, FileText, Link2, MessageSquare,
  Award, TrendingDown, Banknote, CircleDollarSign, Settings,
  Crown, GraduationCap, Scale, Pen, Download
} from 'lucide-react';
import QRCode from 'react-qr-code';
import SoldiersSettingsEditor from '@/components/SoldiersSettingsEditor';
import SocialMediaCards from '@/components/SocialMediaCards';
import SuperSoldierDashboard from '@/components/SuperSoldierDashboard';
import SoldierRecruitmentPlaybook from '@/components/SoldierRecruitmentPlaybook';
import SoldierPayoutTracker from '@/components/SoldierPayoutTracker';

export default function SoldierDashboard() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [payoutSummary, setPayoutSummary] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [agreementStatus, setAgreementStatus] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showPlaybook, setShowPlaybook] = useState(false);

  // Check if user is a soldier OR super admin (super admin has unlimited access)
  const isSuperAdmin = user?.is_super_admin === true;
  const isPendingCertification = user?.is_soldier && user?.soldier_status === 'pending_certification';
  // Allow access if: super admin, active soldier, OR pending certification soldier
  const isSoldier = isSuperAdmin || (user?.is_soldier && user?.soldier_status === 'active');
  const hasAnySoldierAccess = isSuperAdmin || user?.is_soldier; // Can access dashboard if assigned as soldier (any status)
  
  // Check if user is a Super Soldier with edit_settings permission
  const isSuperSoldier = dashboard?.rank === 'super_soldier';
  const canEditSettings = isSuperAdmin || (isSuperSoldier && dashboard?.permissions?.includes('edit_settings'));

  useEffect(() => {
    // Fetch dashboard data if user is an active soldier or super admin
    if (token && isSoldier) {
      fetchDashboard();
      fetchPayoutSummary();
      fetchStripeStatus();
      fetchAgreementStatus();
    } else if (token && isPendingCertification) {
      // For pending certification, just fetch agreement status
      fetchAgreementStatus();
      setLoading(false);
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [token, isSoldier, isPendingCertification, authLoading]);

  const fetchPayoutSummary = async () => {
    try {
      const response = await axios.get(`${API}/soldiers/payout-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayoutSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch payout summary:', error);
    }
  };

  const fetchAgreementStatus = async () => {
    try {
      const response = await axios.get(`${API}/soldier-agreement/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgreementStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch agreement status:', error);
    }
  };

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/soldiers/dashboard/detailed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You are not an approved HairChair Soldier');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeStatus = async () => {
    try {
      const response = await axios.get(`${API}/soldiers/connect/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripeStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch Stripe status:', error);
    }
  };

  const handleConnectStripe = async () => {
    setConnectingStripe(true);
    try {
      const currentUrl = window.location.origin;
      const response = await axios.post(`${API}/soldiers/connect/onboard`, {
        return_url: `${currentUrl}/soldiers/dashboard?stripe=connected`,
        refresh_url: `${currentUrl}/soldiers/dashboard?stripe=refresh`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      window.location.href = response.data.onboarding_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start Stripe onboarding');
      setConnectingStripe(false);
    }
  };

  const handleRequestPayout = async () => {
    if (!stripeStatus?.payouts_enabled) {
      toast.error('Please complete Stripe Connect setup first');
      return;
    }
    
    if (dashboard?.war_chest?.available_now <= 0) {
      toast.error('No available earnings to withdraw');
      return;
    }

    setRequestingPayout(true);
    try {
      const response = await axios.post(`${API}/soldiers/payouts/request`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Payout request of $${response.data.amount.toFixed(2)} submitted!`);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request payout');
    } finally {
      setRequestingPayout(false);
    }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard!');
  };

  const shareLink = async (title, text, url) => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
      } catch (err) {
        copyLink(url);
      }
    } else {
      copyLink(url);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSendingInvite(true);
    try {
      await axios.post(`${API}/soldiers/invite`, {
        email: inviteEmail,
        message: inviteMessage,
        referral_code: dashboard?.soldier?.referral_code
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invite sent to ${inviteEmail}!`);
      setInviteEmail('');
      setInviteMessage('');
    } catch (error) {
      const referralLink = `${window.location.origin}/register?role=stylist&soldier=${dashboard?.soldier?.referral_code}`;
      const subject = encodeURIComponent('Join MyHairChair - Grow Your Beauty Business');
      const body = encodeURIComponent(`${inviteMessage || 'Check out MyHairChair - the ultimate platform for beauty professionals!'}\n\nSign up here: ${referralLink}`);
      window.open(`mailto:${inviteEmail}?subject=${subject}&body=${body}`, '_blank');
      toast.success('Opening email client...');
      setInviteEmail('');
      setInviteMessage('');
    } finally {
      setSendingInvite(false);
    }
  };

  // Demo packages for soldiers to share (excluding investor and dev partnership)
  const demoPackages = [
    {
      id: 'overview',
      title: 'Platform Overview',
      icon: Presentation,
      color: 'from-amber-500 to-yellow-500',
      description: 'Start here - Perfect for introducing MyHairChair',
      badge: 'Start Here',
      badgeColor: 'bg-green-500',
      shareText: 'Check out MyHairChair - the all-in-one platform for beauty professionals!'
    },
    {
      id: 'clients',
      title: 'For Clients',
      icon: User,
      color: 'from-blue-500 to-cyan-500',
      description: 'Show clients how to find stylists and book appointments',
      badge: null,
      shareText: 'Discover the MyHairChair client experience - easy booking, loyalty rewards, and more!'
    },
    {
      id: 'stylists',
      title: 'For Barbers & Stylists',
      icon: Scissors,
      color: 'from-amber-500 to-orange-500',
      description: 'Demonstrate the stylist dashboard and booking management',
      badge: 'Most Popular',
      badgeColor: 'bg-purple-500',
      shareText: 'See how MyHairChair helps barbers and stylists grow their business!'
    },
    {
      id: 'owners',
      title: 'For Salon Owners',
      icon: Building2,
      color: 'from-purple-500 to-pink-500',
      description: 'Showcase booth management and business tools',
      badge: null,
      shareText: 'MyHairChair for salon owners - manage booths, staff, and revenue all in one place!'
    },
    {
      id: 'soldiers',
      title: 'HairChair Soldiers',
      icon: Shield,
      color: 'from-zinc-600 to-zinc-800',
      description: 'Exclusive Founding Partner affiliate program',
      badge: 'EARN',
      badgeColor: 'bg-amber-500',
      shareText: 'Join the HairChair Soldiers affiliate program and earn commissions!'
    }
  ];

  // Show loading while auth is being verified
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-amber-400 mx-auto mb-4 animate-pulse" />
          <p className="text-white text-lg">Loading...</p>
        </div>
      </div>
    );
  }

  // Pending certification state - must complete academy first
  // CHECK THIS BEFORE "not a soldier" so pending soldiers can access
  if (isPendingCertification) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
        {/* Header */}
        <div className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-sm sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate(-1)}
                  className="text-zinc-400 hover:text-white"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <div className="hidden sm:block h-6 w-px bg-zinc-700" />
                <h1 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-400" />
                  Soldier Command Center
                </h1>
              </div>
              <Badge className="bg-yellow-500/20 text-yellow-400">
                <Clock className="w-3 h-3 mr-1" />
                Pending Certification
              </Badge>
            </div>
          </div>
        </div>

        {/* Pending Certification Content */}
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
          <div className="text-center mb-8">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-amber-400/20 to-yellow-500/20 rounded-2xl flex items-center justify-center border-2 border-dashed border-amber-500/50">
              <GraduationCap className="w-12 h-12 text-amber-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Complete Your Training</h1>
            <p className="text-zinc-400 max-w-md mx-auto">
              You&apos;ve been approved as a HairChair Soldier! Complete Soldier Academy to unlock your Command Center and start earning commissions.
            </p>
          </div>

          <Card className="bg-zinc-900/50 border-zinc-700 mb-6">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
                  <BookOpen className="w-6 h-6 text-amber-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-1">Soldier Academy</h3>
                  <p className="text-zinc-400 text-sm mb-4">
                    5 short modules • ~20 minutes total • 80% to pass
                  </p>
                  <ul className="space-y-2 text-sm text-zinc-400 mb-4">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      Learn the MyHairChair platform inside and out
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      Master the client and stylist experience
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      Understand the commission structure
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      Get your Certified Soldier badge
                    </li>
                  </ul>
                  <div className="space-y-3">
                    <Button 
                      onClick={() => navigate('/soldiers/academy')}
                      className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold w-full"
                    >
                      <GraduationCap className="w-4 h-4 mr-2" />
                      Start Soldier Academy
                    </Button>
                    <Button 
                      onClick={() => navigate('/soldiers/agreement')}
                      variant="outline"
                      className="border-zinc-700 text-zinc-300 w-full"
                    >
                      <Scale className="w-4 h-4 mr-2" />
                      View & Sign Agreement
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-800/30 border-zinc-700/50">
            <CardContent className="p-4 text-center">
              <p className="text-zinc-500 text-sm">
                <Lock className="w-4 h-4 inline mr-1" />
                Your referral code and commission tracking will be available after certification
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Not a soldier - show locked state
  if (!isSoldier) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
        {/* Header - matches Demo Package Page style */}
        <div className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-sm sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => navigate(-1)}
                  className="text-zinc-400 hover:text-white"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <div className="hidden sm:block h-6 w-px bg-zinc-700" />
                <h1 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-400" />
                  Soldier Command Center
                </h1>
              </div>
            </div>
          </div>
        </div>

        {/* Diverse Team Banner - MY HAIRCHAIR HQ (Locked State) */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
          <div className="text-center mb-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-white flex items-center justify-center gap-3">
              <Building2 className="w-7 h-7 text-amber-400" />
              MY HAIRCHAIR HQ
            </h2>
            <p className="text-zinc-400 text-sm mt-1">Your command center for building a hair care empire</p>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-xl mb-6">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/f35d2631-7989-4ade-a61d-13dc62e450ea/images/94ac6f334c423deb635538373cb4c16bec168b0672e6ab8e838eb0065e6ae3f0.png"
              alt="Diverse team of professional barbers and hair stylists"
              className="w-full h-[200px] sm:h-[280px] object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
              <p className="text-white font-semibold text-lg">Building the Future Together</p>
              <p className="text-zinc-300 text-sm">Diverse professionals. One powerful platform.</p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          {/* Locked State Hero */}
          <div className="text-center mb-12">
            <div className="w-24 h-24 bg-zinc-800/50 rounded-full flex items-center justify-center mx-auto mb-6 border border-zinc-700">
              <Lock className="w-12 h-12 text-zinc-500" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-3">Currently Not Available</h2>
            <p className="text-zinc-400 max-w-lg mx-auto mb-8">
              The Soldier Command Center is exclusively for approved HairChair Soldiers. 
              Apply to join our elite affiliate program and unlock these features.
            </p>
            
            {/* What You're Missing */}
            <div className="grid sm:grid-cols-3 gap-4 max-w-2xl mx-auto mb-8">
              <div className="bg-zinc-800/30 rounded-xl p-5 border border-zinc-700/50">
                <DollarSign className="w-8 h-8 text-green-400 mx-auto mb-3" />
                <p className="text-white font-semibold">Earn Commissions</p>
                <p className="text-zinc-500 text-sm">10/10/8 payout model</p>
              </div>
              <div className="bg-zinc-800/30 rounded-xl p-5 border border-zinc-700/50">
                <Share2 className="w-8 h-8 text-blue-400 mx-auto mb-3" />
                <p className="text-white font-semibold">Share Demo Links</p>
                <p className="text-zinc-500 text-sm">Personal referral tracking</p>
              </div>
              <div className="bg-zinc-800/30 rounded-xl p-5 border border-zinc-700/50">
                <Gift className="w-8 h-8 text-purple-400 mx-auto mb-3" />
                <p className="text-white font-semibold">Exclusive Bonuses</p>
                <p className="text-zinc-500 text-sm">Founding partner perks</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={() => navigate('/soldiers')}
                className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-semibold px-8"
              >
                <Shield className="w-4 h-4 mr-2" />
                Apply to Become a Soldier
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/demo-package')}
                className="border-zinc-700 text-zinc-300"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview Demo (View Only)
              </Button>
            </div>
          </div>

          {/* Preview of Demo Packages (Locked) */}
          <div>
            <h3 className="text-xl font-semibold text-zinc-400 mb-6 text-center">Demo Packages Preview</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 opacity-50 pointer-events-none">
              {demoPackages.map((pkg) => (
                <Card key={pkg.id} className="bg-zinc-900/50 border-zinc-800 relative overflow-hidden group">
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10">
                    <Lock className="w-10 h-10 text-zinc-500" />
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${pkg.color} flex items-center justify-center`}>
                        <pkg.icon className="w-7 h-7 text-white" />
                      </div>
                      {pkg.badge && (
                        <Badge className={`${pkg.badgeColor} text-white text-xs`}>{pkg.badge}</Badge>
                      )}
                    </div>
                    <h4 className="text-lg font-bold text-white mb-2">{pkg.title}</h4>
                    <p className="text-sm text-zinc-500">{pkg.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center">
        <div className="text-amber-400 flex items-center gap-2">
          <RefreshCw className="w-5 h-5 animate-spin" />
          Loading Command Center...
        </div>
      </div>
    );
  }

  // Error state
  if (!dashboard) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center">
        <Card className="bg-zinc-900 border-zinc-800 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Unable to Load Dashboard</h2>
          <p className="text-zinc-400 mb-4">Please try refreshing the page</p>
          <Button onClick={fetchDashboard} className="bg-amber-500 hover:bg-amber-600 text-black">
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  const referralLink = `${window.location.origin}/register?role=stylist&soldier=${dashboard?.soldier?.referral_code || ''}`;

  // Render individual package detail view
  const renderPackageDetail = () => {
    if (!selectedPackage) return null;

    const pkg = demoPackages.find(p => p.id === selectedPackage);
    if (!pkg) return null;

    const fullLink = `${window.location.origin}/demo?package=${pkg.id}&ref=${dashboard?.soldier?.referral_code || ''}`;

    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <Card className="bg-zinc-900 border-zinc-800 max-w-lg w-full">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${pkg.color} flex items-center justify-center`}>
                  <pkg.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-white">{pkg.title}</CardTitle>
                  <p className="text-sm text-zinc-500">{pkg.description}</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedPackage(null)} className="text-zinc-400">
                ✕
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Shareable Link */}
            <div>
              <Label className="text-zinc-400 text-sm">Your Shareable Link</Label>
              <div className="flex items-center gap-2 bg-zinc-800 rounded-lg p-3 mt-1">
                <input
                  type="text"
                  value={fullLink}
                  readOnly
                  className="flex-1 bg-transparent text-zinc-300 text-sm outline-none"
                />
                <Button size="sm" variant="ghost" onClick={() => copyLink(fullLink)} className="text-amber-400">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => shareLink(pkg.title, pkg.shareText, fullLink)}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Link to={`/demo?package=${pkg.id}`}>
                <Button variant="outline" className="w-full border-zinc-700 text-zinc-300">
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
              </Link>
            </div>

            {/* QR Code */}
            <div className="text-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowQR(!showQR)}
                className="text-zinc-400"
              >
                <QrCode className="w-4 h-4 mr-2" />
                {showQR ? 'Hide' : 'Show'} QR Code
              </Button>
              {showQR && (
                <div className="mt-4 flex flex-col items-center gap-3">
                  <div id="qr-code-demo" className="bg-white p-4 rounded-lg">
                    <QRCode value={fullLink} size={150} />
                  </div>
                  <Button
                    onClick={() => {
                      const svg = document.querySelector('#qr-code-demo svg');
                      if (svg) {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        const svgData = new XMLSerializer().serializeToString(svg);
                        const img = new Image();
                        img.onload = () => {
                          canvas.width = 200;
                          canvas.height = 200;
                          ctx.fillStyle = 'white';
                          ctx.fillRect(0, 0, canvas.width, canvas.height);
                          ctx.drawImage(img, 25, 25, 150, 150);
                          const link = document.createElement('a');
                          link.download = `myhairchair-demo-qr-${pkg.id}.png`;
                          link.href = canvas.toDataURL('image/png');
                          link.click();
                          toast.success('QR Code downloaded!');
                        };
                        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                      }
                    }}
                    size="sm"
                    className="bg-amber-500 hover:bg-amber-600 text-black"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download QR
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950">
      {/* Header - matches Demo Package Page style */}
      <div className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/stylist')}
                className="text-zinc-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="hidden sm:block h-6 w-px bg-zinc-700" />
              <h1 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-400" />
                Soldier Command Center
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={() => setShowPlaybook(true)}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
                data-testid="recruitment-playbook-btn"
              >
                <Presentation className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Recruitment Playbook</span>
                <span className="sm:hidden">Playbook</span>
              </Button>
              <Button 
                onClick={() => navigate('/soldiers/academy')}
                variant="outline" 
                size="sm"
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                data-testid="soldier-academy-btn"
              >
                <GraduationCap className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Soldier Academy</span>
                <span className="sm:hidden">Academy</span>
              </Button>
              <Button 
                onClick={() => navigate('/soldiers/agreement')}
                variant="outline" 
                size="sm"
                className="border-stone-600 text-stone-400 hover:bg-stone-700/50"
                data-testid="soldier-agreement-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Agreement</span>
                <span className="sm:hidden">Agmt</span>
              </Button>
              <Button 
                onClick={() => setShowGuide(true)}
                variant="outline" 
                size="sm"
                className="border-zinc-600 text-zinc-400 hover:bg-zinc-700/50"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Guide</span>
              </Button>
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 hidden sm:flex">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Active Soldier
              </Badge>
              <Button 
                onClick={fetchDashboard}
                variant="outline" 
                size="sm"
                className="border-zinc-700 text-zinc-400"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Diverse Team Banner - MY HAIRCHAIR HQ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
        <div className="text-center mb-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-white flex items-center justify-center gap-3">
            <Building2 className="w-7 h-7 text-amber-400" />
            MY HAIRCHAIR HQ
          </h2>
          <p className="text-zinc-400 text-sm mt-1">Your command center for building a hair care empire</p>
        </div>
        <div className="relative rounded-2xl overflow-hidden shadow-xl mb-6">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/f35d2631-7989-4ade-a61d-13dc62e450ea/images/94ac6f334c423deb635538373cb4c16bec168b0672e6ab8e838eb0065e6ae3f0.png"
            alt="Diverse team of professional barbers and hair stylists"
            className="w-full h-[200px] sm:h-[280px] object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
            <p className="text-white font-semibold text-lg">Building the Future Together</p>
            <p className="text-zinc-300 text-sm">Diverse professionals. One powerful platform.</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Hero Section */}
        <div className="text-center mb-10">
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-4">
            <Shield className="w-3 h-3 mr-1" />
            Founding Partner • {dashboard?.soldier?.referral_code || 'N/A'}
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            {dashboard?.soldier?.salon_name || 'Your Salon'}
          </h2>
          <p className="text-zinc-400 max-w-lg mx-auto">
            Share demo packages with your network and earn commissions on every referral
          </p>
        </div>

        {/* SOLDIER AGREEMENT PROMPT - Soldiers should sign the legal agreement */}
        {agreementStatus && !agreementStatus.has_signed && (
          <div className="mb-8 p-6 bg-gradient-to-r from-purple-500/20 via-violet-500/20 to-purple-500/20 border border-purple-500/40 rounded-2xl">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <FileText className="w-8 h-8 text-purple-400" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl font-bold text-white mb-2 flex items-center justify-center md:justify-start gap-2">
                  <Scale className="w-5 h-5 text-purple-400" />
                  Sign Your Soldier Agreement
                </h3>
                <p className="text-zinc-300 text-sm mb-3">
                  Complete your onboarding by signing the Independent Contractor Agreement. This protects both you and the company, and includes your beneficiary designation for commission inheritance.
                </p>
                <div className="flex flex-wrap gap-2 justify-center md:justify-start text-xs">
                  <span className="px-2 py-1 bg-zinc-800 rounded text-zinc-400">✓ Tax documentation (W-9)</span>
                  <span className="px-2 py-1 bg-zinc-800 rounded text-zinc-400">✓ Death benefits</span>
                  <span className="px-2 py-1 bg-zinc-800 rounded text-zinc-400">✓ Commission terms</span>
                </div>
              </div>
              <div className="flex-shrink-0">
                <Button
                  onClick={() => navigate('/soldiers/agreement')}
                  className="bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white font-bold px-8 py-3 h-auto"
                  data-testid="sign-agreement-btn"
                >
                  <Pen className="w-4 h-4 mr-2" />
                  Sign Agreement
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* STRIPE CONNECT REQUIREMENT BANNER - Soldiers MUST connect to receive payouts */}
        {!stripeStatus?.payouts_enabled && (
          <div className="mb-8 p-6 bg-gradient-to-r from-red-500/20 via-amber-500/20 to-red-500/20 border border-red-500/40 rounded-2xl">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-8 h-8 text-red-400" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl font-bold text-white mb-2 flex items-center justify-center md:justify-start gap-2">
                  <CreditCard className="w-5 h-5 text-amber-400" />
                  Stripe Connect Required to Receive Payouts
                </h3>
                <p className="text-zinc-300 text-sm mb-3">
                  As a HairChair Soldier, you must connect your Stripe account to receive commission payouts. 
                  Without Stripe Connect, you <strong className="text-red-400">cannot receive earnings</strong> from your referrals.
                </p>
                <div className="flex flex-wrap gap-2 justify-center md:justify-start text-xs">
                  <span className="px-2 py-1 bg-zinc-800 rounded text-zinc-400">✓ Secure bank deposits</span>
                  <span className="px-2 py-1 bg-zinc-800 rounded text-zinc-400">✓ Fast payouts</span>
                  <span className="px-2 py-1 bg-zinc-800 rounded text-zinc-400">✓ Tax documentation</span>
                </div>
              </div>
              <div className="flex-shrink-0">
                <Button
                  onClick={handleConnectStripe}
                  disabled={connectingStripe}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold px-8 py-3 h-auto"
                >
                  {connectingStripe ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Banknote className="w-4 h-4 mr-2" />
                      Set Up Stripe Connect
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Soldier Dashboard Stats - Enhanced with Payout Engine */}
        <div className="mb-10">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Wallet className="w-5 h-5 text-amber-400" />
            Your Earnings
          </h3>
          
          {/* Payout Status Legend */}
          <div className="mb-4 p-3 bg-zinc-900/50 rounded-lg border border-zinc-800">
            <p className="text-xs text-zinc-400 mb-2">💡 <strong>How Payouts Work:</strong></p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                <span className="text-zinc-400">Trial (30 days free)</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-yellow-400"></span>
                <span className="text-zinc-400">Pending (Month 1 held)</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-400"></span>
                <span className="text-zinc-400">Released (Month 2+)</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-purple-400"></span>
                <span className="text-zinc-400">Residual (8% ongoing)</span>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Total Earned */}
            <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/10 border-green-500/30">
              <CardContent className="p-4 sm:p-5">
                <DollarSign className="w-6 h-6 text-green-400 mb-2" />
                <p className="text-2xl sm:text-3xl font-bold text-white">
                  ${payoutSummary?.lifetime_earnings?.toFixed(2) || dashboard?.war_chest?.total_earned?.toFixed(2) || '0.00'}
                </p>
                <p className="text-xs text-green-400">Lifetime Earnings</p>
              </CardContent>
            </Card>
            
            {/* Held Amount (Month 1 pending release) */}
            <Card className="bg-zinc-900 border-yellow-500/30">
              <CardContent className="p-4 sm:p-5">
                <Lock className="w-6 h-6 text-yellow-400 mb-2" />
                <p className="text-2xl sm:text-3xl font-bold text-yellow-400">
                  ${payoutSummary?.held_amount?.toFixed(2) || '0.00'}
                </p>
                <p className="text-xs text-zinc-400">Held (Month 1)</p>
                <p className="text-[10px] text-zinc-500 mt-1">Released after 2nd payment</p>
              </CardContent>
            </Card>
            
            {/* Available to Withdraw */}
            <Card className="bg-zinc-900 border-green-500/30">
              <CardContent className="p-4 sm:p-5">
                <Unlock className="w-6 h-6 text-green-400 mb-2" />
                <p className="text-2xl sm:text-3xl font-bold text-green-400">
                  ${payoutSummary?.available_amount?.toFixed(2) || dashboard?.war_chest?.available_now?.toFixed(2) || '0.00'}
                </p>
                <p className="text-xs text-zinc-400">Available</p>
                <p className="text-[10px] text-zinc-500 mt-1">Ready to withdraw</p>
              </CardContent>
            </Card>
            
            {/* Total Referrals */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4 sm:p-5">
                <Users className="w-6 h-6 text-blue-400 mb-2" />
                <p className="text-2xl sm:text-3xl font-bold text-white">
                  {payoutSummary?.total_referrals || dashboard?.referrals?.total || 0}
                </p>
                <p className="text-xs text-zinc-400">Referrals</p>
                {payoutSummary?.trial_referrals > 0 && (
                  <p className="text-[10px] text-blue-400 mt-1">{payoutSummary.trial_referrals} in trial</p>
                )}
              </CardContent>
            </Card>
            
            {/* Stripe Status */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4 sm:p-5">
                {stripeStatus?.payouts_enabled ? (
                  <>
                    <CheckCircle2 className="w-6 h-6 text-green-400 mb-2" />
                    <p className="text-lg font-bold text-green-400">Connected</p>
                    <p className="text-xs text-zinc-400">Stripe Status</p>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-6 h-6 text-amber-400 mb-2" />
                    <Button
                      size="sm"
                      onClick={handleConnectStripe}
                      disabled={connectingStripe}
                      className="bg-amber-500 hover:bg-amber-600 text-black text-xs"
                    >
                      {connectingStripe ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Connect Stripe'}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Payout Button - Updated to use new payout summary */}
          {stripeStatus?.payouts_enabled && (payoutSummary?.available_amount > 0 || dashboard?.war_chest?.available_now > 0) && (
            <div className="mt-4 flex justify-end">
              <Button
                onClick={handleRequestPayout}
                disabled={requestingPayout}
                className="bg-green-500 hover:bg-green-600 text-black"
              >
                {requestingPayout ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <DollarSign className="w-4 h-4 mr-2" />
                )}
                Request Payout (${payoutSummary?.available_amount?.toFixed(2) || dashboard?.war_chest?.available_now?.toFixed(2) || '0.00'})
              </Button>
            </div>
          )}
        </div>

        {/* Payout Tracker - Visual progress for each referral */}
        <div className="mb-10">
          <SoldierPayoutTracker payoutSummary={payoutSummary} loading={!payoutSummary && loading} />
        </div>

        {/* Demo Packages Grid - Main Feature */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-white">Share Demo Packages</h3>
              <p className="text-sm text-zinc-400">Click any package to get your personalized shareable link</p>
            </div>
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
              <Shield className="w-3 h-3 mr-1" />
              Soldier Exclusive
            </Badge>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {demoPackages.map((pkg) => (
              <Card 
                key={pkg.id} 
                onClick={() => setSelectedPackage(pkg.id)}
                className="bg-zinc-900/50 border-zinc-800 hover:border-amber-500/50 transition-all cursor-pointer group relative overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <CardContent className="p-6 relative">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${pkg.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <pkg.icon className="w-7 h-7 text-white" />
                    </div>
                    {pkg.badge && (
                      <Badge className={`${pkg.badgeColor} text-white text-xs`}>{pkg.badge}</Badge>
                    )}
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">
                    {pkg.title}
                  </h4>
                  <p className="text-sm text-zinc-500 mb-4">{pkg.description}</p>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="flex-1 border-zinc-700 text-zinc-300 group-hover:border-amber-500/50">
                      <Share2 className="w-3 h-3 mr-1" />
                      Share
                    </Button>
                    <Button size="sm" variant="ghost" className="text-zinc-500">
                      <Eye className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Your Referral Link Section */}
        <Card className="bg-gradient-to-r from-amber-500/10 to-yellow-500/5 border-amber-500/30 mb-10">
          <CardContent className="p-6">
            <div className="flex flex-col gap-6">
              {/* Header */}
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <QrCode className="w-7 h-7 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Your Referral Links</h3>
                  <p className="text-sm text-zinc-400">Share these links to earn commissions • Users get promotional pricing!</p>
                </div>
              </div>
              
              {/* Promo Pricing Info */}
              <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg p-4">
                <p className="text-green-400 font-semibold text-sm mb-2">🎉 Your code unlocks THE SOLDIER ADVANTAGE:</p>
                
                {/* Visual Pricing Ladder */}
                <div className="mb-4 overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-zinc-700">
                        <th className="py-2 px-2 text-left text-zinc-400">Step</th>
                        <th className="py-2 px-2 text-left text-zinc-400">Timeframe</th>
                        <th className="py-2 px-2 text-center text-amber-400">Pro</th>
                        <th className="py-2 px-2 text-center text-purple-400">Elite</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-zinc-800/50">
                        <td className="py-2 px-2 text-green-400 font-bold">Step 1</td>
                        <td className="py-2 px-2 text-zinc-300">First 30 Days</td>
                        <td className="py-2 px-2 text-center text-green-400 font-bold">FREE</td>
                        <td className="py-2 px-2 text-center text-green-400 font-bold">FREE</td>
                      </tr>
                      <tr className="border-b border-zinc-800/50">
                        <td className="py-2 px-2 text-amber-400 font-bold">Step 2</td>
                        <td className="py-2 px-2 text-zinc-300">Next 3 Months</td>
                        <td className="py-2 px-2 text-center text-white font-bold">$19.99/mo</td>
                        <td className="py-2 px-2 text-center text-white font-bold">$39.99/mo</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-2 text-zinc-400 font-bold">Step 3</td>
                        <td className="py-2 px-2 text-zinc-300">Thereafter</td>
                        <td className="py-2 px-2 text-center text-zinc-300">$29.99/mo</td>
                        <td className="py-2 px-2 text-center text-zinc-300">$49.99/mo</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <p className="text-amber-400 text-xs">💡 Your referrals get 30 days FREE + 3 months at intro pricing. Your commissions start when they make their first payment!</p>
              </div>
              
              {/* Main Referral Link */}
              <div>
                <Label className="text-zinc-400 text-sm mb-2 block">General Signup Link</Label>
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 bg-zinc-900/50 rounded-lg p-3">
                      <input
                        type="text"
                        value={referralLink}
                        readOnly
                        className="flex-1 bg-transparent text-zinc-300 text-sm outline-none"
                      />
                      <Button size="sm" variant="ghost" onClick={() => copyLink(referralLink)} className="text-amber-400">
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowQR(!showQR)}
                      variant="outline"
                      size="sm"
                      className="border-amber-500/50 text-amber-400"
                    >
                      <QrCode className="w-4 h-4 mr-2" />
                      QR
                    </Button>
                    <Button
                      onClick={() => shareLink('Join MyHairChair', `Join MyHairChair with my code and get special pricing! Use code: ${dashboard?.soldier?.referral_code}`, referralLink)}
                      size="sm"
                      className="bg-amber-500 hover:bg-amber-600 text-black"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Look Fest Contest Link */}
              <div>
                <Label className="text-zinc-400 text-sm mb-2 block flex items-center gap-2">
                  <Award className="w-4 h-4 text-purple-400" />
                  Look Fest Contest Link (Higher Conversion!)
                </Label>
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                      <input
                        type="text"
                        value={`${window.location.origin}/register?contest=lookfest&role=stylist&code=${dashboard?.soldier?.referral_code || ''}`}
                        readOnly
                        className="flex-1 bg-transparent text-zinc-300 text-sm outline-none"
                      />
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => copyLink(`${window.location.origin}/register?contest=lookfest&role=stylist&code=${dashboard?.soldier?.referral_code || ''}`)} 
                        className="text-purple-400"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <Button
                    onClick={() => shareLink(
                      'Join the Keep the 100 Look Fest!', 
                      `🔥 Win $100 monthly + $1,000 grand prize! Join with my code for special pricing: ${dashboard?.soldier?.referral_code}`, 
                      `${window.location.origin}/register?contest=lookfest&role=stylist&code=${dashboard?.soldier?.referral_code || ''}`
                    )}
                    size="sm"
                    className="bg-purple-500 hover:bg-purple-600 text-white"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share Contest
                  </Button>
                </div>
              </div>

              {showQR && (
                <div className="flex flex-col items-center gap-3">
                  <div id="qr-code-container" className="bg-white p-4 rounded-lg">
                    <QRCode value={referralLink} size={150} />
                  </div>
                  <Button
                    onClick={() => {
                      const svg = document.querySelector('#qr-code-container svg');
                      if (svg) {
                        const canvas = document.createElement('canvas');
                        const ctx = canvas.getContext('2d');
                        const svgData = new XMLSerializer().serializeToString(svg);
                        const img = new Image();
                        img.onload = () => {
                          canvas.width = 200;
                          canvas.height = 200;
                          ctx.fillStyle = 'white';
                          ctx.fillRect(0, 0, canvas.width, canvas.height);
                          ctx.drawImage(img, 25, 25, 150, 150);
                          const link = document.createElement('a');
                          link.download = `myhairchair-qr-${dashboard?.soldier?.referral_code || 'code'}.png`;
                          link.href = canvas.toDataURL('image/png');
                          link.click();
                          toast.success('QR Code downloaded!');
                        };
                        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                      }
                    }}
                    size="sm"
                    className="bg-amber-500 hover:bg-amber-600 text-black"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download QR Code
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Send Invites Section */}
        <Card className="bg-zinc-900 border-zinc-800 mb-10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-400" />
              Send Personal Invites
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <Label className="text-zinc-300">Email Address</Label>
                  <Input
                    type="email"
                    placeholder="stylist@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Personal Message (Optional)</Label>
                  <Textarea
                    placeholder="Hey! I think you'd love MyHairChair..."
                    value={inviteMessage}
                    onChange={(e) => setInviteMessage(e.target.value)}
                    rows={3}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
                <Button
                  onClick={handleSendInvite}
                  disabled={sendingInvite || !inviteEmail}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                >
                  {sendingInvite ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Invite
                </Button>
              </div>
              
              {/* Quick Templates */}
              <div>
                <p className="text-sm text-zinc-400 mb-3">Quick Templates</p>
                <div className="space-y-2">
                  {[
                    { title: 'For Stylists', message: "Hey! I've been using MyHairChair to manage my bookings and clients - it's amazing! You should check it out." },
                    { title: 'For Barbers', message: "What's up! MyHairChair has been a game-changer for my barbershop business. Think you'd love it too!" },
                    { title: 'For Salon Owners', message: "Hi! I wanted to share MyHairChair with you - it's perfect for managing booth rentals and staff." }
                  ].map((template) => (
                    <button
                      key={template.title}
                      onClick={() => setInviteMessage(template.message)}
                      className="w-full p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg text-left hover:border-amber-500/50 transition-colors"
                    >
                      <p className="text-sm font-medium text-white">{template.title}</p>
                      <p className="text-xs text-zinc-500 line-clamp-1">{template.message}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Professional Social Media Cards for Soldier Recruitment */}
        <Card className="bg-zinc-900 border-zinc-800 mb-10">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-400" />
              Recruitment Marketing Cards
            </CardTitle>
            <p className="text-stone-400 text-sm">Professional social media cards with your QR code for recruiting stylists</p>
          </CardHeader>
          <CardContent>
            <SocialMediaCards 
              userId={dashboard?.soldier?.user_id || user?.user_id}
              userName={dashboard?.soldier?.owner_name || user?.name}
              referralCode={dashboard?.soldier?.referral_code}
              isSoldier={true}
            />
          </CardContent>
        </Card>

        {/* Super Soldier Platoon Dashboard - Only for Super Soldiers */}
        {isSuperSoldier && (
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  Super Soldier Command
                  <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                    Power 2 Tier
                  </Badge>
                </h3>
                <p className="text-zinc-400 text-sm">Manage your platoon and earn override commissions</p>
              </div>
            </div>
            <SuperSoldierDashboard superSoldierData={dashboard} />
          </div>
        )}

        {/* Recent Referrals */}
        {dashboard.referrals?.list?.length > 0 && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-400" />
                Recent Referrals
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboard.referrals.list.slice(0, 5).map((referral, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        referral.status === 'active' ? 'bg-green-500/20' :
                        referral.status === 'churned' ? 'bg-red-500/20' :
                        'bg-blue-500/20'
                      }`}>
                        {referral.status === 'active' ? (
                          <UserCheck className="w-5 h-5 text-green-400" />
                        ) : referral.status === 'churned' ? (
                          <UserX className="w-5 h-5 text-red-400" />
                        ) : (
                          <Clock className="w-5 h-5 text-blue-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-white">{referral.stylist_email}</p>
                        <p className="text-xs text-zinc-500">
                          {new Date(referral.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={
                        referral.status === 'active' ? 'bg-green-500/20 text-green-400' :
                        referral.status === 'churned' ? 'bg-red-500/20 text-red-400' :
                        'bg-blue-500/20 text-blue-400'
                      }>
                        {referral.status}
                      </Badge>
                      <p className="text-sm text-green-400 mt-1">
                        ${((referral.month1_bonus || 0) + (referral.month2_payout || 0) + (referral.lifetime_commission || 0)).toFixed(2)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              {dashboard.referrals.list.length > 5 && (
                <div className="mt-4 text-center">
                  <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400">
                    View All {dashboard.referrals.total} Referrals
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Soldiers Settings (Super Soldiers with permission or Super Admin) */}
        {canEditSettings && (
          <SoldiersSettingsEditor 
            token={token} 
            isSuperAdmin={isSuperAdmin} 
            canEdit={canEditSettings} 
          />
        )}
      </div>

      {/* Package Detail Modal */}
      {renderPackageDetail()}

      {/* Soldier Guide Modal */}
      {showGuide && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 overflow-y-auto">
          <div className="min-h-screen py-8 px-4">
            <div className="max-w-4xl mx-auto">
              {/* Guide Header */}
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Soldier Success Guide</h2>
                    <p className="text-zinc-400">Everything you need to maximize your earnings</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowGuide(false)}
                  className="text-zinc-400 hover:text-white"
                >
                  ✕
                </Button>
              </div>

              {/* Quick Stats Reminder */}
              <Card className="bg-gradient-to-r from-amber-500/10 to-yellow-500/5 border-amber-500/30 mb-8">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-amber-400 mb-4 flex items-center gap-2">
                    <CircleDollarSign className="w-5 h-5" />
                    Your Earning Potential
                  </h3>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-black/20 rounded-lg">
                      <p className="text-3xl font-bold text-green-400">$10-$15</p>
                      <p className="text-sm text-zinc-400">Month 1 Bonus</p>
                    </div>
                    <div className="text-center p-4 bg-black/20 rounded-lg">
                      <p className="text-3xl font-bold text-green-400">$20-$30</p>
                      <p className="text-sm text-zinc-400">Total (M1+M2)</p>
                    </div>
                    <div className="text-center p-4 bg-black/20 rounded-lg">
                      <p className="text-3xl font-bold text-amber-400">8%</p>
                      <p className="text-sm text-zinc-400">Lifetime Commission</p>
                    </div>
                  </div>
                  <p className="text-sm text-zinc-500 mt-4 text-center">
                    Pro referrals: $10 + $10 + 8% lifetime • Elite referrals: $15 + $15 + 8% lifetime
                  </p>
                </CardContent>
              </Card>

              {/* Guide Sections */}
              <div className="space-y-6">
                
                {/* Section 1: How to Refer */}
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                        <Target className="w-4 h-4 text-blue-400" />
                      </div>
                      Step 1: How to Refer Stylists & Barbers
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                        <div className="flex items-center gap-2 mb-2">
                          <Link2 className="w-4 h-4 text-amber-400" />
                          <h4 className="font-semibold text-white">Share Your Link</h4>
                        </div>
                        <p className="text-sm text-zinc-400 mb-3">
                          Your unique referral link tracks all sign-ups back to you automatically.
                        </p>
                        <div className="bg-zinc-900 rounded p-2 text-xs text-zinc-500 font-mono break-all">
                          {`${window.location.origin}/register?soldier=${dashboard?.soldier?.referral_code || 'YOUR-CODE'}`}
                        </div>
                      </div>
                      <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                        <div className="flex items-center gap-2 mb-2">
                          <QrCode className="w-4 h-4 text-amber-400" />
                          <h4 className="font-semibold text-white">Use Your QR Code</h4>
                        </div>
                        <p className="text-sm text-zinc-400">
                          Perfect for in-person networking at salons, barbershops, trade shows, and beauty events. Print it or show on your phone.
                        </p>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                      <h4 className="font-semibold text-blue-400 mb-2 flex items-center gap-2">
                        <Zap className="w-4 h-4" />
                        Pro Tips for More Referrals
                      </h4>
                      <ul className="text-sm text-zinc-300 space-y-2">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span><strong>Talk to booth renters</strong> - They need scheduling & payment tools</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span><strong>Visit local barbershops</strong> - Offer to demo the app on your phone</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span><strong>Post on social media</strong> - Share your demo links with your network</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span><strong>Attend beauty trade shows</strong> - Bronner Bros, IBS, local events</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span><strong>Connect with barber schools</strong> - Students need booking tools</span>
                        </li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>

                {/* Section 2: Share Demo Packages */}
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                        <Presentation className="w-4 h-4 text-purple-400" />
                      </div>
                      Step 2: Share Demo Packages
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-zinc-400">
                      Use the demo packages above to show prospects exactly what MyHairChair offers. Each package has your referral code embedded!
                    </p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div className="p-3 bg-zinc-800/50 rounded-lg">
                        <Presentation className="w-5 h-5 text-amber-400 mb-2" />
                        <p className="text-sm font-medium text-white">Platform Overview</p>
                        <p className="text-xs text-zinc-500">Best for first-time introductions</p>
                      </div>
                      <div className="p-3 bg-zinc-800/50 rounded-lg">
                        <Scissors className="w-5 h-5 text-orange-400 mb-2" />
                        <p className="text-sm font-medium text-white">For Barbers & Stylists</p>
                        <p className="text-xs text-zinc-500">Most popular - show the dashboard</p>
                      </div>
                      <div className="p-3 bg-zinc-800/50 rounded-lg">
                        <Building2 className="w-5 h-5 text-purple-400 mb-2" />
                        <p className="text-sm font-medium text-white">For Salon Owners</p>
                        <p className="text-xs text-zinc-500">Booth management & analytics</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Section 2B: How to Recruit - NOT MLM Clarification */}
                <Card className="bg-zinc-900 border-zinc-800 ring-2 ring-amber-500/30">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-white flex items-center gap-2">
                        <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center">
                          <UserPlus className="w-4 h-4 text-amber-400" />
                        </div>
                        How to Recruit Successfully
                      </CardTitle>
                      <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Important</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    
                    {/* NOT MLM Clarification */}
                    <div className="p-4 bg-gradient-to-r from-amber-500/10 to-yellow-500/5 rounded-lg border border-amber-500/30">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-bold text-amber-400 mb-2">This is NOT Multi-Level Marketing (MLM)</h4>
                          <p className="text-sm text-zinc-300 mb-3">
                            As a HairChair Soldier, you are recruiting <strong>subscribers</strong> — not other recruiters. 
                            You earn commissions only from people who sign up and pay for MyHairChair subscriptions through your referral link.
                          </p>
                          <div className="grid sm:grid-cols-2 gap-3 text-sm">
                            <div className="flex items-start gap-2">
                              <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                              <span className="text-zinc-400">You earn from <strong className="text-white">direct referrals only</strong></span>
                            </div>
                            <div className="flex items-start gap-2">
                              <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                              <span className="text-zinc-400">No &quot;downline&quot; or &quot;levels&quot;</span>
                            </div>
                            <div className="flex items-start gap-2">
                              <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                              <span className="text-zinc-400"><strong className="text-white">Lifetime commissions</strong> on active subscribers</span>
                            </div>
                            <div className="flex items-start gap-2">
                              <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                              <span className="text-zinc-400">No inventory or products to sell</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Who to Recruit */}
                    <div>
                      <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                        <Target className="w-4 h-4 text-blue-400" />
                        Who Should You Recruit?
                      </h4>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                          <p className="font-medium text-white mb-1">Ideal Candidates</p>
                          <ul className="text-sm text-zinc-400 space-y-1">
                            <li>• Licensed barbers & cosmetologists</li>
                            <li>• Booth renters seeking booking tools</li>
                            <li>• Independent stylists building clientele</li>
                            <li>• Salon/barbershop owners (Elite tier)</li>
                            <li>• Beauty school graduates</li>
                          </ul>
                        </div>
                        <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                          <p className="font-medium text-white mb-1">Required Qualifications</p>
                          <ul className="text-sm text-zinc-400 space-y-1">
                            <li>• Active beauty professional or owner</li>
                            <li>• Valid email for account creation</li>
                            <li>• Accepts clients/appointments</li>
                            <li>• Needs scheduling or payment tools</li>
                            <li>• Ready to try at discounted rate</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Step-by-Step Recruitment Process */}
                    <div>
                      <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-purple-400" />
                        Step-by-Step Recruitment Process
                      </h4>
                      <div className="space-y-3">
                        <div className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-lg border-l-4 border-amber-500">
                          <div className="w-7 h-7 bg-amber-500 rounded-full flex items-center justify-center text-black font-bold text-sm flex-shrink-0">1</div>
                          <div>
                            <p className="font-medium text-white">Identify & Approach</p>
                            <p className="text-sm text-zinc-400">Find beauty professionals in your network, at events, or in local shops. Introduce yourself and ask about their current booking/payment setup.</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-lg border-l-4 border-amber-500">
                          <div className="w-7 h-7 bg-amber-500 rounded-full flex items-center justify-center text-black font-bold text-sm flex-shrink-0">2</div>
                          <div>
                            <p className="font-medium text-white">Demo the Platform</p>
                            <p className="text-sm text-zinc-400">Use the demo packages to show them exactly how MyHairChair works. Focus on features that solve their specific pain points.</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-lg border-l-4 border-amber-500">
                          <div className="w-7 h-7 bg-amber-500 rounded-full flex items-center justify-center text-black font-bold text-sm flex-shrink-0">3</div>
                          <div>
                            <p className="font-medium text-white">Share Your Referral Link</p>
                            <p className="text-sm text-zinc-400">Send them your personalized link or show them the QR code. Emphasize THE SOLDIER ADVANTAGE — $10/mo off for 3 months!</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-green-500/10 rounded-lg border-l-4 border-green-500">
                          <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center text-black font-bold text-sm flex-shrink-0">4</div>
                          <div>
                            <p className="font-medium text-green-400">FIRST FOLLOW-UP (Within 48 Hours)</p>
                            <p className="text-sm text-zinc-400">Check in to see if they signed up, started exploring, or have questions. Offer to walk them through any features. <strong className="text-white">This touchpoint is critical!</strong></p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-green-500/10 rounded-lg border-l-4 border-green-500">
                          <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center text-black font-bold text-sm flex-shrink-0">5</div>
                          <div>
                            <p className="font-medium text-green-400">SECOND FOLLOW-UP (Day 5-6 of Trial)</p>
                            <p className="text-sm text-zinc-400">Before their trial ends, reach out to get feedback. Ask what they like, what could be better, and if they plan to subscribe. <strong className="text-white">Your feedback helps us improve!</strong></p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-lg border-l-4 border-amber-500">
                          <div className="w-7 h-7 bg-amber-500 rounded-full flex items-center justify-center text-black font-bold text-sm flex-shrink-0">6</div>
                          <div>
                            <p className="font-medium text-white">Celebrate & Support</p>
                            <p className="text-sm text-zinc-400">When they subscribe, congratulate them! Stay available to answer questions. Happy referrals stay longer = more lifetime commissions for you.</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Follow-Up Importance */}
                    <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                      <h4 className="font-semibold text-blue-400 mb-2 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        Why Follow-Up Twice? (Minimum!)
                      </h4>
                      <p className="text-sm text-zinc-300 mb-3">
                        Studies show that <strong>80% of sales require 5+ follow-ups</strong>, yet most people give up after one. 
                        By contacting your prospects at least twice, you:
                      </p>
                      <ul className="text-sm text-zinc-400 space-y-2">
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span>Show you genuinely care about their success</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span>Gather valuable feedback to share with our team</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span>Catch them before the trial expires (and they forget)</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span>Build relationships that lead to more referrals from them</span>
                        </li>
                      </ul>
                    </div>

                    {/* What to Say Scripts */}
                    <div>
                      <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-pink-400" />
                        Sample Scripts
                      </h4>
                      <div className="space-y-3">
                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                          <p className="text-xs text-amber-400 mb-1 font-semibold">FIRST FOLLOW-UP (48 hours after sign-up)</p>
                          <p className="text-sm text-zinc-300 italic">
                            &quot;Hey [Name]! Just checking in — did you get a chance to explore MyHairChair? 
                            I&apos;d love to hear what you think so far. Let me know if you have any questions!&quot;
                          </p>
                        </div>
                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                          <p className="text-xs text-green-400 mb-1 font-semibold">SECOND FOLLOW-UP (Day 5-6 of trial)</p>
                          <p className="text-sm text-zinc-300 italic">
                            &quot;Hey [Name]! Your trial is ending soon — wanted to check in and get your honest feedback. 
                            What did you like? Anything you wish was different? Your input really helps us improve the platform!&quot;
                          </p>
                        </div>
                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                          <p className="text-xs text-purple-400 mb-1 font-semibold">IF THEY HAVEN&apos;T SIGNED UP YET</p>
                          <p className="text-sm text-zinc-300 italic">
                            &quot;No pressure at all! If timing isn&apos;t right, I totally understand. 
                            Just wanted to make sure you had the link if you change your mind. The free trial is always there when you&apos;re ready!&quot;
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Your Lifetime Benefit */}
                    <div className="p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/5 rounded-lg border border-green-500/30">
                      <h4 className="font-semibold text-green-400 mb-2 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Your Lifetime Financial Benefit
                      </h4>
                      <p className="text-sm text-zinc-300 mb-3">
                        Every subscriber you recruit generates <strong>ongoing passive income</strong> for as long as they remain a paying member:
                      </p>
                      <div className="grid sm:grid-cols-3 gap-3 text-center">
                        <div className="p-3 bg-black/20 rounded-lg">
                          <p className="text-2xl font-bold text-green-400">$20-$30</p>
                          <p className="text-xs text-zinc-500">Upfront Bonus (M1+M2)</p>
                        </div>
                        <div className="p-3 bg-black/20 rounded-lg">
                          <p className="text-2xl font-bold text-amber-400">8%</p>
                          <p className="text-xs text-zinc-500">Every Month Forever</p>
                        </div>
                        <div className="p-3 bg-black/20 rounded-lg">
                          <p className="text-2xl font-bold text-blue-400">$28-$38+</p>
                          <p className="text-xs text-zinc-500">Per Referral Year 1</p>
                        </div>
                      </div>
                      <p className="text-xs text-zinc-500 mt-3 text-center">
                        Example: 10 Pro referrals who stay 2 years = $200 upfront + $576 lifetime = <strong className="text-green-400">$776 total</strong>
                      </p>
                    </div>

                  </CardContent>
                </Card>

                {/* Section 3: Connect Stripe */}
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-4 h-4 text-green-400" />
                      </div>
                      Step 3: Connect Stripe for Payouts
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className={`p-4 rounded-lg border ${stripeStatus?.payouts_enabled ? 'bg-green-500/10 border-green-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
                      <div className="flex items-center gap-3 mb-3">
                        {stripeStatus?.payouts_enabled ? (
                          <>
                            <CheckCircle2 className="w-6 h-6 text-green-400" />
                            <div>
                              <p className="font-semibold text-green-400">Stripe Connected!</p>
                              <p className="text-sm text-zinc-400">You&apos;re ready to receive payouts</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="w-6 h-6 text-amber-400" />
                            <div>
                              <p className="font-semibold text-amber-400">Stripe Not Connected</p>
                              <p className="text-sm text-zinc-400">Connect now to receive your earnings</p>
                            </div>
                          </>
                        )}
                      </div>
                      {!stripeStatus?.payouts_enabled && (
                        <Button
                          onClick={() => { setShowGuide(false); handleConnectStripe(); }}
                          className="w-full bg-green-500 hover:bg-green-600 text-black"
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Connect Stripe Now
                        </Button>
                      )}
                    </div>

                    <div className="space-y-3">
                      <h4 className="font-semibold text-white">How Stripe Connect Works:</h4>
                      <div className="space-y-2">
                        <div className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-lg">
                          <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center text-black font-bold text-sm flex-shrink-0">1</div>
                          <div>
                            <p className="text-sm text-white font-medium">Click &quot;Connect Stripe&quot;</p>
                            <p className="text-xs text-zinc-500">You&apos;ll be redirected to Stripe&apos;s secure onboarding</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-lg">
                          <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center text-black font-bold text-sm flex-shrink-0">2</div>
                          <div>
                            <p className="text-sm text-white font-medium">Complete Verification</p>
                            <p className="text-xs text-zinc-500">Enter your bank details and verify your identity</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 p-3 bg-zinc-800/50 rounded-lg">
                          <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center text-black font-bold text-sm flex-shrink-0">3</div>
                          <div>
                            <p className="text-sm text-white font-medium">Receive Payouts</p>
                            <p className="text-xs text-zinc-500">Weekly payouts every Friday to your bank account</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Section 4: Understanding Your Earnings */}
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center">
                        <Banknote className="w-4 h-4 text-amber-400" />
                      </div>
                      Step 4: Understanding Your Earnings
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-zinc-700">
                            <th className="text-left py-2 text-zinc-400">Timeline</th>
                            <th className="text-center py-2 text-zinc-400">Pro ($29.99/mo)</th>
                            <th className="text-center py-2 text-zinc-400">Elite ($49.99/mo)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-zinc-800">
                            <td className="py-3 text-white">Month 1 Payment</td>
                            <td className="py-3 text-center text-green-400">$10 pending</td>
                            <td className="py-3 text-center text-green-400">$15 pending</td>
                          </tr>
                          <tr className="border-b border-zinc-800">
                            <td className="py-3 text-white">Month 2 Payment</td>
                            <td className="py-3 text-center text-green-400">+$10 = $20 released</td>
                            <td className="py-3 text-center text-green-400">+$15 = $30 released</td>
                          </tr>
                          <tr>
                            <td className="py-3 text-white">Month 3+ (Lifetime)</td>
                            <td className="py-3 text-center text-amber-400">8% (~$2.40/mo)</td>
                            <td className="py-3 text-center text-amber-400">8% (~$4.00/mo)</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="p-4 bg-zinc-800/50 rounded-lg">
                      <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                        <HelpCircle className="w-4 h-4 text-amber-400" />
                        Why the 45-Day Vetting Period?
                      </h4>
                      <p className="text-sm text-zinc-400">
                        To protect against fraud, your Month 1 + Month 2 bonuses are held for 45 days from the referral&apos;s first payment. 
                        Once cleared, the full amount is released to your available balance. Lifetime commissions (8%) start immediately after Month 2.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Section 5: Requesting Payouts */}
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                        <Wallet className="w-4 h-4 text-emerald-400" />
                      </div>
                      Step 5: Requesting Payouts
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="p-4 bg-zinc-800/50 rounded-lg">
                        <Clock className="w-5 h-5 text-blue-400 mb-2" />
                        <p className="font-medium text-white">Payout Schedule</p>
                        <p className="text-sm text-zinc-400">Weekly payouts every Friday</p>
                      </div>
                      <div className="p-4 bg-zinc-800/50 rounded-lg">
                        <DollarSign className="w-5 h-5 text-green-400 mb-2" />
                        <p className="font-medium text-white">Minimum Payout</p>
                        <p className="text-sm text-zinc-400">$10 minimum to request</p>
                      </div>
                    </div>
                    <p className="text-sm text-zinc-400">
                      Once you have available earnings and Stripe is connected, click the <strong className="text-green-400">&quot;Request Payout&quot;</strong> button 
                      on your dashboard. Funds typically arrive in your bank within 2-3 business days.
                    </p>
                  </CardContent>
                </Card>

                {/* Section 6: FAQ */}
                <Card className="bg-zinc-900 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <div className="w-8 h-8 bg-pink-500/20 rounded-lg flex items-center justify-center">
                        <MessageSquare className="w-4 h-4 text-pink-400" />
                      </div>
                      Frequently Asked Questions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      <div className="p-4 bg-zinc-800/50 rounded-lg">
                        <p className="font-medium text-white mb-1">What happens if my referral cancels?</p>
                        <p className="text-sm text-zinc-400">
                          If a referral cancels before Month 2, you won&apos;t receive the bonus. If they cancel after, you keep what you&apos;ve earned 
                          but lifetime commissions stop for that referral.
                        </p>
                      </div>
                      <div className="p-4 bg-zinc-800/50 rounded-lg">
                        <p className="font-medium text-white mb-1">Can I refer salon owners too?</p>
                        <p className="text-sm text-zinc-400">
                          Yes! Salon owners who subscribe to Elite ($49.99/mo) earn you higher bonuses: $15 + $15 + 8% lifetime (~$4/mo).
                        </p>
                      </div>
                      <div className="p-4 bg-zinc-800/50 rounded-lg">
                        <p className="font-medium text-white mb-1">How do I track my referrals?</p>
                        <p className="text-sm text-zinc-400">
                          Your dashboard shows all referrals in real-time: pending, converting (Month 1), active (Month 2+), and churned.
                        </p>
                      </div>
                      <div className="p-4 bg-zinc-800/50 rounded-lg">
                        <p className="font-medium text-white mb-1">Is there a limit to how many I can refer?</p>
                        <p className="text-sm text-zinc-400">
                          No limit! The more stylists and barbers you bring to MyHairChair, the more you earn. Top soldiers are earning $1,000+/month.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Need Help */}
                <Card className="bg-gradient-to-r from-amber-500/10 to-yellow-500/5 border-amber-500/30">
                  <CardContent className="p-6 text-center">
                    <Award className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                    <h3 className="text-xl font-bold text-white mb-2">Need Help?</h3>
                    <p className="text-zinc-400 mb-4">
                      Our team is here to support your success. Reach out anytime!
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button
                        variant="outline"
                        className="border-amber-500/50 text-amber-400"
                        onClick={() => window.open('mailto:soldiers@myhairchair.com', '_blank')}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        soldiers@myhairchair.com
                      </Button>
                      <Button
                        onClick={() => setShowGuide(false)}
                        className="bg-amber-500 hover:bg-amber-600 text-black"
                      >
                        Got It - Start Earning!
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Close Button at Bottom */}
              <div className="mt-8 text-center">
                <Button
                  variant="ghost"
                  onClick={() => setShowGuide(false)}
                  className="text-zinc-400"
                >
                  Close Guide
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Soldier Recruitment Playbook */}
      <SoldierRecruitmentPlaybook 
        isOpen={showPlaybook}
        onClose={() => setShowPlaybook(false)}
        referralCode={dashboard?.soldier?.referral_code || 'YOUR-CODE'}
        discountValue="14 days FREE + $10 off for 3 months"
      />
    </div>
  );
}
